"use client"

import { createContext, PropsWithChildren, useContext, useEffect, useMemo, useState } from "react";


type Step = {
    number: number;
    isCompleted: boolean;
}

type StepperContextType = {
    currentStep: Step;
    stepsList: Step[];
    setStepCompleted: (step: number) => void;
    setCurrentStep: (step: number) => void;
    totalNumberOfSteps: number;
}

const StepperContext = createContext<StepperContextType | null>(null);

export const StepperContextProvider = ({
    children,
    numberOfSteps
}: PropsWithChildren & { numberOfSteps: number }) => {
    const initialStepsList: Step[] = useMemo(() => {
        return Array.from({ length: numberOfSteps }, (_, index) => ({
            number: index + 1,
            isCompleted: false,
        }))
    }, [numberOfSteps])

    const [stepsList, setStepsList] = useState<Step[]>(initialStepsList)
    const [currentStep, setCurrentStep] = useState<Step>(stepsList[0])
    const totalNumberOfSteps = stepsList.length


    useEffect(() => {
        const contentWrapper = document.getElementById('dashboard-content-wrapper-id')
        if (contentWrapper) {
            contentWrapper.scrollTo({ top: 0, behavior: 'smooth' })
        }
    }, [currentStep.number])

    const updateCurrentStep = (step: number) => {
        if (step > totalNumberOfSteps) throw new Error("Step number is greater than total steps")
        setCurrentStep(stepsList[step - 1])
    }

    const setStepCompleted = (step: number) => {
        if (currentStep.number < totalNumberOfSteps) {
            setStepsList((prev) => {
                prev[step - 1].isCompleted = true
                return prev
            })
        }
    }

    return (
        <StepperContext.Provider value={{
            currentStep,
            setStepCompleted,
            setCurrentStep: updateCurrentStep,
            stepsList,
            totalNumberOfSteps,
        }}>
            {children}
        </StepperContext.Provider>
    )
}


export const useStepperContext = () => {
    const context = useContext(StepperContext);
    if (!context) {
        throw new Error('useStepperContext must be used within StepperContextProvider');
    }
    return context;
}

